import type {EndUser, EndUserKeyMedia, EndUserOwnerInfo,} from "./euscp/euscp"

export interface KeyMediaDevice {
  typeIndex: number
  devIndex: number
  devName: string
  typeName: string
}

/** A qualified trust services provider from `CAs.json`; `issuerCN` is what `readPrivateKey*`'s `caCN` expects. */
export interface CertificateAuthority {
  issuerCN: string
  label: string
}

export default class EUSignLibraryAdapter {
  library: EndUser
  private keyMedias: EndUserKeyMedia[] = []

  constructor(library: EndUser) {
    this.library = library
  }

  async readPrivateKey(
    typeIndex: number,
    devIndex: number,
    password: string,
    caCN?: string
  ): Promise<EndUserOwnerInfo> {
    const keyMedia = this.keyMedias.find(
      (km) => km.typeIndex === typeIndex && km.devIndex === devIndex
    )
    if (!keyMedia) {
      throw new Error("Носій ос. ключа не знайдено")
    }
    return this.library.ReadPrivateKey({ ...keyMedia, password }, undefined, caCN)
  }

  async isPrivateKeyRead(): Promise<boolean> {
    return this.library.IsPrivateKeyReaded()
  }

  /** Dev-only file-token path (Test/JS/main.js's PK_FORM_TYPE_FILE): reads a private key straight from an uploaded key file instead of a hardware medium. */
  async readPrivateKeyFile(
    privateKey: Uint8Array,
    password: string,
    caCN?: string
  ): Promise<EndUserOwnerInfo> {
    return this.library.ReadPrivateKeyBinary(
      privateKey,
      password,
      undefined,
      caCN
    )
  }

  async signWithData(content: Uint8Array): Promise<string> {
    const signature = await this.library.SignDataInternal(true, content, true)
    return signature as string
  }

  async sign(content: Uint8Array): Promise<string> {
    const signature = await this.library.SignData(content, true)
    return signature as string
  }

  /**
   * The providers the library was initialized with (`CAs.json`). Passing one's
   * `issuerCN` as `caCN` to `readPrivateKey*` makes the library request the key's
   * certificate from that provider only, instead of polling every CA in turn.
   */
  async getCAs(): Promise<CertificateAuthority[]> {
    const cas = await this.library.GetCAs()
    return cas
      .filter((ca) => ca.issuerCNs.length > 0)
      .map((ca) => ({ issuerCN: ca.issuerCNs[0], label: ca.issuerCNs[0] }))
  }

  async enumKeys(): Promise<KeyMediaDevice[]> {
    this.keyMedias = await this.library.GetKeyMedias()
    return this.keyMedias.map((km) => ({
      typeIndex: km.typeIndex,
      devIndex: km.devIndex,
      devName: km.device,
      typeName: km.type,
    }))
  }
}
